import { lazy, Suspense } from 'react';

import { ListSkeleton } from '@/shared/components/skeletons';
import { Skeleton } from '@/shared/ui/skeleton';

import type { EntityType } from './api';
import { LeadsOff } from './components/panel/LeadsOff';
import { useCrmConfig } from './hooks';

/*
 * The heavy parts of the module (cards, forms, the profile tab) load on demand; these wrappers are what the registry
 * renders.
 */

const RecordPanel = lazy(() => import('./components/panel/RecordPanel'));
const ProfileTab = lazy(async () => ({ default: (await import('./components/ProfileTab')).ProfileCrmTab }));

function PanelFallback() {
  return (
    <div className="space-y-4 p-6" aria-busy>
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-8 w-full" />
      <ListSkeleton rows={4} avatar={false} />
    </div>
  );
}

function LazyRecordPanel({ type, id }: { type: EntityType; id: string }) {
  return (
    <Suspense fallback={<PanelFallback />}>
      <RecordPanel type={type} id={id} />
    </Suspense>
  );
}

export function LazyLeadPanel({ id }: { id: string }) {
  const config = useCrmConfig();
  if (config.isLoading) return <PanelFallback />;
  return config.leadsEnabled ? <LazyRecordPanel type="lead" id={id} /> : <LeadsOff />;
}

export function LazyDealPanel({ id }: { id: string }) {
  return <LazyRecordPanel type="deal" id={id} />;
}

export function LazyContactPanel({ id }: { id: string }) {
  return <LazyRecordPanel type="contact" id={id} />;
}

export function LazyCompanyPanel({ id }: { id: string }) {
  return <LazyRecordPanel type="company" id={id} />;
}

export function LazyQuotePanel({ id }: { id: string }) {
  return <LazyRecordPanel type="quote" id={id} />;
}

export function LazyInvoicePanel({ id }: { id: string }) {
  return <LazyRecordPanel type="invoice" id={id} />;
}

export function LazyItemPanel({ id }: { id: string }) {
  return <LazyRecordPanel type="item" id={id} />;
}

export function LazyProfileTab({ userId }: { userId: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={4} />}>
      <ProfileTab userId={userId} />
    </Suspense>
  );
}
